import React, { createContext, useContext, useEffect } from "react";
import { buttonStyleVars, ThemeConfig, setThemeVars } from "@servicepro/jobsite-components";

interface ThemeContextValue {
  theme: ThemeConfig;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within ThemeProvider");
  }
  return context;
};

interface ThemeProviderProps {
  theme: ThemeConfig;
  children: React.ReactNode;
}

/**
 * Map database theme fields to theme-vars keys.
 *
 * `buttonStyle` is a stored preset, not a CSS var, so it isn't part of
 * ThemeConfig — it expands into three vars here.
 */
function mapThemeToVars(theme: ThemeConfig & { buttonStyle?: string | null }) {
  return {
    // Colors
    colorBackground: theme.colorBackground,
    colorForeground: theme.colorForeground,
    colorCard: theme.colorCard,
    colorCardForeground: theme.colorCardForeground,
    colorPopover: theme.colorPopover,
    colorPopoverForeground: theme.colorPopoverForeground,
    colorPrimary: theme.colorPrimary,
    colorPrimaryForeground: theme.colorPrimaryForeground,
    colorSecondary: theme.colorSecondary,
    colorSecondaryForeground: theme.colorSecondaryForeground,
    colorMuted: theme.colorMuted,
    colorMutedForeground: theme.colorMutedForeground,
    colorAccent: theme.colorAccent,
    colorAccentForeground: theme.colorAccentForeground,
    colorDestructive: theme.colorDestructive,
    colorDestructiveForeground: theme.colorDestructiveForeground,
    colorBorder: theme.colorBorder,
    colorInput: theme.colorInput,
    colorRing: theme.colorRing,

    // Fonts
    fontFamilyHeading: theme.fontFamilyHeading,
    fontFamilyBody: theme.fontFamilyBody,
    fontWeightHeading: theme.fontWeightHeading,
    ...buttonStyleVars(theme.buttonStyle),
    fontWeightBody: theme.fontWeightBody,

    // Layout
    radius: theme.radius,
  };
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ theme, children }) => {
  useEffect(() => {
    // Skip if running on server
    if (typeof window === "undefined") return;

    // Skip if critical CSS already set the theme (SSG with pre-rendered styles)
    const criticalCss = document.getElementById("critical-css");
    if (criticalCss) return;

    // No theme means the build-time/fallback theme already applies
    if (!theme) return;

    // Map database theme fields to theme-vars keys and apply them
    setThemeVars(mapThemeToVars(theme));
  }, [theme]);

  return <ThemeContext.Provider value={{ theme }}>{children}</ThemeContext.Provider>;
};
